<template>
  <intersection-observer
    :id="productId"
    class="product-card"
    :active="activateLogging"
    @intersect="emitIntersect"
  >
    <nuxt-link
      class="product-card__link"
      :class="{ 'product-card__link-no-side-padding': !sidePadding }"
      :to="link"
      @click.native="onClickLink"
    >
      <div class="product-card__image-container">
        <div class="product-card__image">
          <extended-image
            :src="image"
            css-width="100%"
            css-height="100%"
            has-loading
            object-fit="contain"
          />
        </div>
        <div
          v-for="(note, index) in notes"
          :key="index"
          class="product-card__limit"
        >
          {{ note }}
        </div>
      </div>
      <div class="product-card__body">
        <div class="product-card__top-container">
          <p class="product-card__title">{{ title }}</p>
          <div v-if="price" class="product-card__price">
            {{ price }}<span>円</span>
            <span v-if="taxIncluded" class="product-card__tax">{{
              `(税込 ${taxIncluded}円)`
            }}</span>
          </div>
        </div>
        <div class="product-card__limit-pc-container">
          <div
            v-for="(note, index) in notes"
            :key="index"
            class="product-card__limit-pc"
          >
            {{ note }}
          </div>
        </div>
      </div>
    </nuxt-link>
  </intersection-observer>
</template>

<script setup>
import ExtendedImage from '@/components/ExtendedImage'
import IntersectionObserver from '@/components/IntersectionObserver'

import { useImpressionStore } from '../stores/impression';
const impressionStore = useImpressionStore()

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  /**
   * 画像用データ
   */
  image: {
    type: String,
    required: true,
  },
  price: {
    type: [Number, String],
    default: null,
  },
  taxIncluded: {
    type: [Number, String],
    default: null,
  },
  notes: {
    type: Array,
    default: () => [],
  },
  jan: {
    type: String,
    required: true,
  },
  shopId: {
    type: String,
    default: null,
  },
  flyerId: {
    type: Number,
    default: null,
  },
  detailId: {
    type: Number,
    default: null,
  },
  productGroupId: {
    type: Number,
    default: null,
  },
  sidePadding: {
    type: Boolean,
    default: true,
  },
  /**
   * インプレッション計測の有効化
   */
  activateLogging: {
    type: Boolean,
    default: false,
  },
  location: {
    type: Object,
    default: null,
  },
  utmSource: {
    type: [Number, String, null],
    default: null,
  },
})

const emit = defineEmits(['intersect'])

const link = computed(() => {
  if (props.flyerId === null && props.detailId === null) {
    if (props.utmSource) {
      return `/product/?jan=${props.jan}&shop-id=${props.shopId}&product-group-id=${props.productGroupId}&utm_source=${props.utmSource}`
    }
    return `/product/?jan=${props.jan}&shop-id=${props.shopId}&product-group-id=${props.productGroupId}`
  }
  if (props.utmSource) {
    return `/product/?jan=${props.jan}&shop-id=${props.shopId}&flyer-id=${props.flyerId}&detail-id=${props.detailId}&product-group-id=${props.productGroupId}&utm_source=${props.utmSource}`
  }
  return `/product/?jan=${props.jan}&shop-id=${props.shopId}&flyer-id=${props.flyerId}&detail-id=${props.detailId}&product-group-id=${props.productGroupId}`
})

const productId = computed(() => {
  return `product-${props.jan}`
})

const emitIntersect = () => {
  const data = {
    id: props.jan,
    groupId: props.productGroupId,
    themeIndex: 0,
    productIndex: 0,
  }
  if (props.location) {
    data.themeIndex = props.location.area_line_number - 1
    data.productIndex = props.location.element_line_number - 1
  }
  emit('intersect', data)
}

const onClickLink = () => {
  impressionStore.setLocation(props.location)
}
</script>

<style lang="scss" scoped>
.product-card {
  box-sizing: border-box;
  position: relative;

  .product-card__body {
    font-size: 1.4rem;
    line-height: calc(21 / 14);
    font-weight: 400;
    padding: 0 0 0 0.8rem;
    flex-grow: 1;
    @media screen and (min-width: $tablet-breakpoint) {
      padding-left: 1.6rem;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }

    span {
      font-size: 1.2rem;
      line-height: calc(17.38 / 12);
      font-weight: normal;
    }
  }

  .product-card__image {
    position: relative;
    width: 14.1rem;
    height: 9.4rem;
    border-radius: 0.4rem;
    flex-shrink: 0;
    overflow: hidden;
    @media screen and (min-width: $tablet-breakpoint) {
      width: 24rem;
      height: 16rem;
      border: solid 0.05rem $color-line;
    }
  }

  .product-card__limit {
    padding: 0.4rem;
    border-radius: 0.4rem;
    border: 0.1rem solid $color-line;
    text-align: center;
    margin-top: 0.4rem;
    @media screen and (min-width: $tablet-breakpoint) {
      display: none;
    }
  }

  .product-card__limit-pc {
    display: none;
    @media screen and (min-width: $tablet-breakpoint) {
      display: inline-block;
      width: 14rem;
      padding: 0.4rem 0;
      border-radius: 0.4rem;
      border: 0.05rem solid $color-line;
      text-align: center;
      margin: auto 0.8rem 0 0;
      font-weight: 400;
      line-height: 1;
      font-size: 1.2rem;
      background-color: $color-white;
      box-sizing: border-box;
    }
  }

  .product-card__link {
    color: $color-main-text;
    display: flex;
    position: relative;
    justify-content: space-between;
    height: 100%;
    padding: 1.6rem 1.6rem 1.6rem 0;
    margin-left: 1.6rem;
    border-top: 0.05rem solid $color-line;
    @media screen and (min-width: $tablet-breakpoint) {
      margin: 0;
      padding: 1.6rem;
    }
  }

  .product-card__link-no-side-padding {
    @media screen and (min-width: $tablet-breakpoint) {
      padding: 1.6rem 0;
    }
  }

  .product-card__price {
    font-weight: 900;
    font-size: 2rem;
    line-height: 1.448;
    color: $color-alert;
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: flex-end;
    margin-top: 0.4rem;
    text-align: end;
    width: 100%;
    @media screen and (min-width: $tablet-breakpoint) {
      margin: 0.8rem 0 1.6rem 0;
    }
  }
  .product-card__secondary-text {
    font-size: 1.2rem;
    line-height: 1.3616;
    font-weight: 400;
    margin-top: 0.2rem;
  }
  .product-card__tax {
    color: $color-gray-text;
    width: 100%;
    text-align: right;
  }

  &:last-child {
    .product-card__link {
      @media screen and (min-width: $tablet-breakpoint) {
        border-bottom: 0.05rem solid $color-line;
      }
    }
  }
}
</style>
